import { DOCUMENT, OBJECT } from '@pkg/config/sanity/schemas';
import { baseTextBlockConfig } from '@pkg/modular-blocks/blocks/inner/BaseText';
import { buttonGroupBlockConfig } from '@pkg/modular-blocks/blocks/inner/ButtonGroup';
import { salesforceFormBlockConfig } from '@pkg/modular-blocks/blocks/inner/SalesforceForm';
import { calloutBlockConfig } from '@pkg/modular-blocks/blocks/inner/Callout';
import { faqBlockConfig } from '@pkg/modular-blocks/blocks/inner/FAQ';
import { formsBlockConfig } from '@pkg/modular-blocks/blocks/inner/Forms';
import { imageBlockConfig } from '@pkg/modular-blocks/blocks/inner/Image';
import { CmsPeopleList, blockPeopleListFields } from '@pkg/modular-blocks/blocks/inner/PeopleList';
import { CmsProfilePicture, blockProfilePictureFields } from '@pkg/modular-blocks/blocks/inner/ProfilePicture';
import { CmsLocationFinder, blockLocationFinderFields } from '@pkg/modular-blocks/blocks/outer/LocationFinder';
import { partnerLogoBlockConfig } from '@pkg/modular-blocks/blocks/inner/PartnerLogo';
import { CmsRawHtml } from '@pkg/modular-blocks/blocks/inner/RawHtml';
import { solutionTilesBlockConfig } from '@pkg/modular-blocks/blocks/inner/SolutionTiles';
import { statisticsBlockConfig } from '@pkg/modular-blocks/blocks/inner/Statistics';
import { tickerBlockConfig } from '@pkg/modular-blocks/blocks/inner/Ticker';
import { tilesBlockConfig } from '@pkg/modular-blocks/blocks/inner/Tiles';
import { CmsNudge, blockNudgeFields } from '@pkg/modular-blocks/blocks/inner/Nudge';
import { titleBlockConfig, blockTitleFields } from '@pkg/modular-blocks/blocks/inner/Title';
import { videoBlockConfig } from '@pkg/modular-blocks/blocks/inner/Video';
import { bagBlockConfig } from '@pkg/modular-blocks/blocks/outer/BagSection';
import { CmsPrivacyControlsSection, blockPrivacyControlsSectionFields } from '@pkg/modular-blocks/blocks/outer/PrivacyControls';
import { ctaSectionBlockConfig } from '@pkg/modular-blocks/blocks/outer/CTASection';
import { carouselSectionBlockConfig } from '@pkg/modular-blocks/blocks/outer/CarouselSection';
import { CmsCookieList, blockCookieListFields } from '@pkg/modular-blocks/blocks/outer/CookieList';
import { CmsSignatureGenerator, blockSignatureGeneratorFields } from '@pkg/modular-blocks/blocks/outer/SignatureGenerator';
import { CmsCampaignCounter, blockCampaignCounterFields } from '@pkg/modular-blocks/blocks/inner/CampaignCounter';
import { CmsSimpleCounter, blockSimpleCounterFields } from '@pkg/modular-blocks/blocks/inner/SimpleCounter';
import { mainHeroBlockConfig } from '@pkg/modular-blocks/blocks/outer/MainHero';
import { CmsCareersBlock, blockCareersFields } from '@pkg/modular-blocks/blocks/outer/Careers';
import { CmsImageVideoBlock, blockImageVideoFields } from '@pkg/modular-blocks/blocks/outer/ImageVideo';
import { sectionBlockConfig } from '@pkg/modular-blocks/blocks/outer/Section';
import { referralBlockConfig } from '@pkg/modular-blocks/blocks/outer/Referral';
import { solutionFinderBlockConfig } from '@pkg/modular-blocks/blocks/outer/SolutionFinder';
import { stepsSectionBlockConfig } from '@pkg/modular-blocks/blocks/outer/StepsSection';
import { subHeroBlockConfig } from '@pkg/modular-blocks/blocks/outer/SubHeroSection';
import { tickerSectionBlockConfig } from '@pkg/modular-blocks/blocks/outer/TickerSection';
import { CmsSurpriseBagFinder, blockSurpriseBagFinder } from '@pkg/modular-blocks/blocks/outer/SurpriseBagFinder';
import { CmsListingBlock, blockListingFields } from '@pkg/modular-blocks/blocks/inner/Listing';
import { defineAsyncComponent, type Component } from 'vue';
import { blockSupportSectionFields, CmsSupportSectionBlock } from '@pkg/modular-blocks/blocks/outer/SupportSection';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import type { ModularBlocksConfig } from '@pkg/modular-blocks/frontend/types';
import PagesTemplate from '~/app/features/cms/components/templates/Pages.vue';
import PostsTemplate from '~/app/features/cms/components/templates/Posts.vue';
import BrandProfilePage from '~/app/features/brandProfile/BrandProfilePage.vue';

export type { ModularBlockConfig, ModularBlocksConfig } from '@pkg/modular-blocks/frontend/types';

export interface ComponentMap {
  [key: string]: Component;
}

/**
 * The default Page Template for schemas not defined in `schemaTypesToComponents`.
 */
export const defaultTemplateComponent: Component = PagesTemplate;

/**
 * Default page template components to use for Sanity schema types.
 */
export const schemaTypesToComponents: ComponentMap = {
  [DOCUMENT.PAGES]: PagesTemplate,
  [DOCUMENT.ABOUT_FOOD_WASTE_BLOG]: PostsTemplate,
  [DOCUMENT.PRESS_RELEASES]: PostsTemplate,
  [DOCUMENT.SURPRISE_BAG_PAGES]: PagesTemplate,
  [DOCUMENT.BRAND_PROFILE]: BrandProfilePage,
};

export const modularBlocksConfig: ModularBlocksConfig = {
  modularRenderer: ModularBlocksRenderer,
  outer: {
    // Outer Blocks
    [OBJECT.MODULAR_OUTER_BLOCK_SECTION]: sectionBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_HERO]: mainHeroBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_SUB_HERO]: subHeroBlockConfig,
    [OBJECT.MODULAR_OUTER_IMAGE_VIDEO]: {
      name: 'outer-block-image-video',
      component: CmsImageVideoBlock,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/ImageOrVideo.vue'),
      ),
      fields: blockImageVideoFields,
    },
    [OBJECT.MODULAR_OUTER_BLOCK_CTA]: ctaSectionBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_CAROUSEL_SECTION]: carouselSectionBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_TICKER_SECTION]: tickerSectionBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_STEPS_SECTION]: stepsSectionBlockConfig,
    [OBJECT.MODULAR_OUTER_SOLUTION_FINDER]: solutionFinderBlockConfig,
    [OBJECT.MODULAR_OUTER_SUPPORT_SECTION]: {
      name: 'outer-block-support-section',
      component: CmsSupportSectionBlock,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/SupportSection.vue'),
      ),
      fields: blockSupportSectionFields,
    },
    [OBJECT.MODULAR_OUTER_BLOCK_BAG]: bagBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_REFERRAL]: referralBlockConfig,
    [OBJECT.MODULAR_OUTER_BLOCK_CAREERS]: {
      name: 'outer-block-careers',
      component: CmsCareersBlock,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/Careers.vue'),
      ),
      fields: blockCareersFields,
    },
    [OBJECT.MODULAR_OUTER_BLOCK_SIGNATURE_GENERATOR]: {
      name: 'outer-block-signature-generator',
      component: CmsSignatureGenerator,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/SignatureGenerator.vue'),
      ),
      fields: blockSignatureGeneratorFields,
    },
    [OBJECT.MODULAR_OUTER_BLOCK_COOKIE_LIST]: {
      name: 'outer-block-cookie-list',
      component: CmsCookieList,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/CookieList.vue'),
      ),
      fields: blockCookieListFields,
    },
    [OBJECT.MODULAR_OUTER_BLOCK_SURPRISE_BAG]: {
      name: 'outer-block-surprise-bag',
      component: CmsSurpriseBagFinder,
      // Lazy: heaviest block component, and the only consumer of `qrcode`.
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/SurpriseBag/SurpriseBag.vue'),
      ),
      fields: blockSurpriseBagFinder,
    },
    [OBJECT.MODULAR_OUTER_BLOCK_LOCATION_FINDER]: {
      name: 'outer-block-location-finder',
      component: CmsLocationFinder,
      fields: blockLocationFinderFields,
      // Lazy: pulls the Google Maps JS API loader.
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/base/LocationFinder.vue'),
      ),
    },
    [OBJECT.MODULAR_OUTER_BLOCK_PRIVACY_CONTROLS]: {
      name: 'outer-block-privacy-controls',
      component: CmsPrivacyControlsSection,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/PrivacyControlsBlock.vue'),
      ),
      fields: blockPrivacyControlsSectionFields,
    },
  },

  // Inner Blocks
  inner: {
    [OBJECT.MODULAR_INNER_BLOCK_TITLE]: titleBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_BASE_TEXT]: baseTextBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_BASE_BUTTON_GROUP]: buttonGroupBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_SALESFORCE_FORMS]: salesforceFormBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_FAQ]: faqBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_PEOPLE]: {
      name: 'inner-block-people',
      component: CmsPeopleList,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/PeopleList.vue'),
      ),
      fields: blockPeopleListFields,
    },
    [OBJECT.MODULAR_INNER_BLOCK_STATISTICS]: statisticsBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_TICKER]: tickerBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_CALLOUT]: calloutBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_FORMS]: formsBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_VIDEO]: videoBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_IMAGE]: imageBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_PROFILE_PICTURE]: {
      name: 'inner-block-profile-picture',
      component: CmsProfilePicture,
      fields: blockProfilePictureFields,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/base/ProfilePicture.vue'),
      ),
    },
    [OBJECT.MODULAR_INNER_BLOCK_PARTNER_LOGO]: partnerLogoBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_TILES]: tilesBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_NUDGE]: {
      name: 'inner-block-nudge',
      component: CmsNudge,
      fields: blockNudgeFields,
      // Lazy: one of two consumers of the shared embla-carousel UI.
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/Nudge.vue'),
      ),
    },
    [OBJECT.MODULAR_INNER_BLOCK_SOLUTION_TILES]: solutionTilesBlockConfig,
    [OBJECT.MODULAR_INNER_BLOCK_LISTING]: {
      name: 'inner-block-listing',
      component: CmsListingBlock,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/Listing.vue'),
      ),
      fields: blockListingFields,
    },
    [OBJECT.MODULAR_INNER_BLOCK_RAW_HTML]: {
      name: 'inner-block-raw-html',
      component: CmsRawHtml,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/base/RawHtml/RawHtml.vue'),
      ),
      fields: blockTitleFields,
    },
    [OBJECT.MODULAR_INNER_BLOCK_COUNTER_HERO]: {
      name: 'inner-block-counter-hero',
      component: CmsCampaignCounter,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/CampaignCounter.vue'),
      ),
      fields: blockCampaignCounterFields,
    },
    [OBJECT.MODULAR_INNER_BLOCK_SIMPLE_COUNTER]: {
      name: 'inner-block-simple-counter',
      component: CmsSimpleCounter,
      innerComponent: defineAsyncComponent(() =>
        import('~/app/components/blocks/SimpleCounter.vue'),
      ),
      fields: blockSimpleCounterFields,
    },
  },
};
